import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { HttpClientService } from "./http-client.service";
import {
  ExternalSalesmanCard,
  ExternalSalesmanCardImportResult,
  ExternalSalesmanCardListFilters,
  ExternalSalesmanCardListResult,
  ExternalSalesmanCardStatus,
  SalesmanEmailUpdateResponse,
} from "../interfaces/external-salesman-card/external-salesman-card.interface";

@Injectable()
export class ExternalSalesmanCardsService {
  constructor(private http: HttpClientService) {}

  // GET /api/external_salesman_cards
  // GET /api/external_salesman_cards?status=pending_request&search=nunez&page=1
  public list(
    filters?: ExternalSalesmanCardListFilters | ExternalSalesmanCardStatus
  ): Observable<ExternalSalesmanCardListResult> {
    return this.requestList(filters).pipe(tap((_) => console.log("ExternalSalesmanCardsService.list: Ok.")));
  }

  // Same endpoint as list() with include_counts=true → { data, total_count, counts }.
  // counts are absolute status tallies and ignore search/status/page.
  public listWithCounts(
    filters?: ExternalSalesmanCardListFilters | ExternalSalesmanCardStatus
  ): Observable<ExternalSalesmanCardListResult> {
    return this.requestList(filters, true).pipe(
      tap((_) => console.log("ExternalSalesmanCardsService.listWithCounts: Ok."))
    );
  }

  // PUT /api/external_salesman_cards/:id
  public updateStatus(id: number, status: ExternalSalesmanCardStatus): Observable<ExternalSalesmanCard> {
    return this.http
      .put(`/api/external_salesman_cards/${id}`, { status })
      .pipe(tap((_) => console.log("ExternalSalesmanCardsService.updateStatus: Ok.")));
  }

  // PUT /api/external_salesman_cards/:id/salesman_email
  public updateSalesmanEmail(id: number, salesmanEmail: string): Observable<SalesmanEmailUpdateResponse> {
    return this.http
      .put(`/api/external_salesman_cards/${id}/salesman_email`, { salesman_email: salesmanEmail })
      .pipe(tap((_) => console.log("ExternalSalesmanCardsService.updateSalesmanEmail: Ok.")));
  }

  // GET /api/external_salesman_cards/pending_csv
  // GET /api/external_salesman_cards/pending_csv?ids[]=5&ids[]=6
  public pendingCsv(ids?: readonly number[]): Observable<Blob> {
    const options: { responseType: "json"; params?: { "ids[]": string[] } } = {
      responseType: "blob" as "json",
    };
    if (ids?.length) {
      options.params = { "ids[]": ids.map(String) };
    }
    return this.http
      .get<Blob>("/api/external_salesman_cards/pending_csv", options)
      .pipe(tap((_) => console.log("ExternalSalesmanCardsService.pendingCsv: Ok.")));
  }

  // POST /api/external_salesman_card_imports (multipart field: file)
  public importFromFile(file: File): Observable<ExternalSalesmanCardImportResult> {
    const formData = new FormData();
    formData.append("file", file);
    return this.http
      .post("/api/external_salesman_card_imports", formData)
      .pipe(tap((_) => console.log("ExternalSalesmanCardsService.importFromFile: Ok.")));
  }

  private requestList(
    filters?: ExternalSalesmanCardListFilters | ExternalSalesmanCardStatus,
    includeCounts = false
  ): Observable<ExternalSalesmanCardListResult> {
    const normalized = this.normalizeListFilters(filters);
    const params = this.buildListParams(normalized, includeCounts);
    const options = Object.keys(params).length ? { params } : {};
    return this.http.get<ExternalSalesmanCardListResult>("/api/external_salesman_cards", options).pipe(
      map((response) => ({
        data: response?.data ?? [],
        total_count: response?.total_count ?? 0,
        counts: response?.counts,
      }))
    );
  }

  // Accepts the legacy `list(status)` signature and the new filters object.
  private normalizeListFilters(
    filters?: ExternalSalesmanCardListFilters | ExternalSalesmanCardStatus
  ): ExternalSalesmanCardListFilters | undefined {
    if (filters == null) {
      return undefined;
    }
    if (typeof filters === "string") {
      return { status: filters };
    }
    return filters;
  }

  private buildListParams(
    filters?: ExternalSalesmanCardListFilters,
    includeCounts = false
  ): { [param: string]: string } {
    const params: { [param: string]: string } = {};
    if (filters?.status) {
      params.status = filters.status;
    }
    if (filters?.search != null && filters.search !== "") {
      params.search = filters.search;
    }
    if (filters?.page != null && filters.page > 0) {
      params.page = String(filters.page);
    }
    if (includeCounts) {
      params.include_counts = "true";
    }
    return params;
  }
}
